import { h, ref, resolveComponent, type Ref, type Component } from 'vue'
import { useRoute, useRouter } from 'vue-router'

export interface SortState {
  id: string
  desc: boolean
}

export interface UseSortableTableOptions {
  /** Sync sorting state to URL query params (?sort=col&desc=1) */
  syncToUrl?: boolean
  /** Initial sort state */
  initialSort?: SortState | null
  /** Callback when sort changes (e.g., for server-side sorting) */
  onSortChange?: (sort: SortState | null) => void
}

// Validate sort column IDs to prevent injection (allows letters, numbers, underscores, hyphens)
const SORT_ID_RE = /^[a-zA-Z_][a-zA-Z0-9_-]{0,63}$/

/**
 * Composable for table sorting with optional URL sync.
 *
 * Extracts the duplicated sortableHeader pattern from admin views.
 * Supports both client-side and server-side sorting.
 */
export function useSortableTable(options: UseSortableTableOptions = {}) {
  const { syncToUrl = false, initialSort = null, onSortChange } = options

  // Resolve components at setup time (composable is called during setup)
  const UButton = resolveComponent('UButton') as Component

  // Always call composables unconditionally (Rules of Hooks)
  const route = useRoute()
  const router = useRouter()

  // Initialize from URL if syncing, otherwise use initialSort
  function getInitialSort(): SortState | null {
    if (syncToUrl) {
      const sortParam = route.query.sort
      const descParam = route.query.desc
      if (typeof sortParam === 'string' && SORT_ID_RE.test(sortParam)) {
        return {
          id: sortParam,
          desc: descParam === '1' || descParam === 'true',
        }
      }
    }
    return initialSort
  }

  const sorting = ref<SortState | null>(getInitialSort())

  // Update URL when sort changes
  function updateUrl() {
    if (!syncToUrl) return

    const query = { ...route.query }
    if (sorting.value) {
      query.sort = sorting.value.id
      query.desc = sorting.value.desc ? '1' : '0'
    } else {
      delete query.sort
      delete query.desc
    }
    router.replace({ query })
  }

  /**
   * Toggle sort for a column.
   * Cycle: none -> asc -> desc -> none
   */
  function toggleSort(columnId: string) {
    const current = sorting.value
    if (!current || current.id !== columnId) {
      sorting.value = { id: columnId, desc: false }
    } else if (!current.desc) {
      sorting.value = { id: columnId, desc: true }
    } else {
      sorting.value = null
    }
    updateUrl()
    onSortChange?.(sorting.value)
  }

  /** Clear sorting */
  function clearSort() {
    sorting.value = null
    updateUrl()
    onSortChange?.(sorting.value)
  }

  /**
   * Factory function for sortable table column headers.
   * Returns a render function for use in table column definitions.
   */
  function sortableHeader(label: string, columnId: string) {
    // Must match TanStack header signature: ({ column }) => VNode
    // We don't use column since we manage sorting ourselves for server-side
    return ({ column: _column }: { column: unknown }) => {
      void _column // unused, we use our own sorting state
      const isActive = sorting.value?.id === columnId
      const isDesc = sorting.value?.desc
      const icon = isActive
        ? isDesc
          ? 'i-lucide-arrow-down-wide-narrow'
          : 'i-lucide-arrow-up-narrow-wide'
        : 'i-lucide-arrow-up-down'

      return h(UButton, {
        color: 'neutral',
        variant: 'ghost',
        label,
        icon,
        class: '-mx-2.5',
        onClick: (e: Event) => {
          e.preventDefault()
          toggleSort(columnId)
        },
      })
    }
  }

  /**
   * Client-side sort helper.
   * Sorts an array based on current sorting state.
   *
   * @param data - Array to sort
   * @param accessors - Map of column ID to value accessor function
   * @returns Sorted array (new reference)
   */
  function clientSort<T>(
    data: T[],
    accessors: Record<string, (item: T) => string | number | null | undefined>,
  ): T[] {
    if (!sorting.value) return data

    const { id, desc } = sorting.value
    const accessor = accessors[id]
    if (!accessor) return data

    return [...data].sort((a, b) => {
      const aVal = accessor(a)
      const bVal = accessor(b)

      // Handle null/undefined - sort to end in ascending, start in descending
      if (aVal == null) return desc ? -1 : 1
      if (bVal == null) return desc ? 1 : -1

      // Compare values
      if (aVal < bVal) return desc ? 1 : -1
      if (aVal > bVal) return desc ? -1 : 1
      return 0
    })
  }

  return {
    /** Current sort state (null if no sorting) */
    sorting: sorting as Ref<SortState | null>,
    /** Toggle sort for a column */
    toggleSort,
    /** Clear sorting */
    clearSort,
    /** Factory for sortable column headers */
    sortableHeader,
    /** Client-side sort helper */
    clientSort,
  }
}
